"use client";

import { MapPin } from "@phosphor-icons/react";
import Image from "next/image";
import Link from "next/link";
import { type CSSProperties, useEffect, useState } from "react";
import firstPurchaseSpinner from "@/public/images/campaigns/first-purchase-spinner.jpg";
import partnerResultsSpinner from "@/public/images/campaigns/partner-results-spinner.jpg";
import { cn } from "@/shared/libs/commonUtils";

interface Props {
  destination?: string;
  className?: string;
  isComplete?: boolean;
  progress?: number;
  campaignHref?: string;
  campaignLabel?: string;
  isB2BUser?: boolean;
  totalDurationMs?: number;
  onDone?: () => void;
}

export function SearchLoadingExperience({
  destination = "مقصد انتخابی",
  className,
  isComplete,
  progress: controlledProgress,
  campaignHref,
  campaignLabel = "کمپین همکاری در فروش معین درباری",
  isB2BUser: initialIsB2BUser,
  totalDurationMs = 5000,
  onDone,
}: Props) {
  const [progress, setProgress] = useState(0);
  const [isB2BUser, setIsB2BUser] = useState(Boolean(initialIsB2BUser));
  const displayProgress = controlledProgress ?? progress;
  const bannerImage = isB2BUser ? partnerResultsSpinner : firstPurchaseSpinner;
  const bannerAlt = isB2BUser ? campaignLabel : "هدیه خرید اول معین درباری";
  const shouldLinkToCampaign = isB2BUser && Boolean(campaignHref);

  useEffect(() => {
    if (typeof initialIsB2BUser === "boolean") {
      setIsB2BUser(initialIsB2BUser);
      return;
    }

    const controller = new AbortController();

    fetch("/api/profile/me", {
      headers: {
        Accept: "application/json",
      },
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((profile: ProfileRes | null) => {
        setIsB2BUser(Boolean(profile?.results?.is_sign));
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [initialIsB2BUser]);

  useEffect(() => {
    if (isComplete) {
      setProgress(100);
      return;
    }

    const start = Date.now();
    const total = totalDurationMs;
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min((elapsed / total) * 100, 100);
      const easedPct = Math.round(8 + easeOutCubic(pct / 100) * 84);
      setProgress(onDone ? pct : Math.min(easedPct, 92));
      if (pct >= 100) {
        clearInterval(interval);
        if (onDone) setTimeout(onDone, 300);
      }
    }, 120);

    return () => clearInterval(interval);
  }, [isComplete, onDone, totalDurationMs]);

  const campaignBanner = (
    <Image
      src={bannerImage}
      alt={bannerAlt}
      className="h-auto w-full rounded-2xl object-cover shadow-lg ring-1 ring-black/5 transition-transform duration-300 hover:scale-[1.01]"
      loading="eager"
      priority
      sizes="(max-width: 768px) 80vw, 360px"
    />
  );

  return (
    <div
      className={cn(
        "fixed inset-0 z-[200] flex items-center justify-center px-4",
        className,
      )}
      dir="rtl"
      style={
        {
          "--search-loader-background": "#f0f4f8",
          "--search-loader-foreground": "#0d1b2a",
          "--search-loader-card": "#ffffff",
          "--search-loader-primary": "#0a3d6b",
          "--search-loader-secondary": "#e8f0fb",
          "--search-loader-muted": "#edf1f7",
          "--search-loader-muted-foreground": "#6b7c93",
          "--search-loader-accent": "#e85d2f",
          background: "rgba(240,244,248,0.24)",
          backdropFilter: "blur(1.5px)",
        } as CSSProperties
      }
    >
      <div className="w-full max-w-md mx-4">
        <div
          className="overflow-hidden rounded-3xl shadow-2xl"
          style={{ background: "rgba(255,255,255,0.97)" }}
        >
          <div className="h-1.5 bg-gray-100">
            <div
              className="h-full transition-all duration-100 ease-linear"
              style={{
                width: `${displayProgress}%`,
                background:
                  "linear-gradient(to left, var(--search-loader-accent), var(--search-loader-primary))",
              }}
            />
          </div>

          <div className="px-7 pb-6 pt-7">
            <div className="mb-7 flex flex-col items-center text-center">
              <div className="relative mb-5 flex h-20 w-20 items-center justify-center">
                <div
                  className="absolute inset-0 rounded-full opacity-20"
                  style={{
                    animation: "pulse-ring 1.8s ease-out infinite",
                    background: "var(--search-loader-primary)",
                  }}
                />
                <div
                  className="absolute inset-2 rounded-full opacity-10"
                  style={{
                    animation: "pulse-ring 1.8s ease-out 0.6s infinite",
                    background: "var(--search-loader-primary)",
                  }}
                />
                <div
                  className="flex h-14 w-14 items-center justify-center rounded-full shadow-lg"
                  style={{ background: "var(--search-loader-primary)" }}
                >
                  <MapPin
                    size={26}
                    className="text-white"
                    style={{ animation: "bob 2s ease-in-out infinite" }}
                    weight="fill"
                  />
                </div>
              </div>

              <h2
                className="mb-2"
                style={{
                  color: "var(--search-loader-foreground)",
                  fontSize: "1.15rem",
                  fontWeight: 700,
                }}
              >
                در حال جستجوی هتل‌های {destination}
                <span className="search-loader-dots" aria-hidden="true">
                  <span>·</span>
                  <span>·</span>
                  <span>·</span>
                </span>
              </h2>
            </div>

            <div className="mb-7">
              {shouldLinkToCampaign ? (
                <Link
                  href={campaignHref as string}
                  className="block rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--search-loader-accent)] focus-visible:ring-offset-4 focus-visible:ring-offset-white"
                  aria-label={campaignLabel}
                >
                  {campaignBanner}
                </Link>
              ) : (
                campaignBanner
              )}
            </div>
          </div>
        </div>

        <p
          className="mt-4 text-center"
          style={{
            color: "var(--search-loader-muted-foreground)",
            fontSize: "0.72rem",
          }}
        >
          این عملیات معمولاً کمتر از ۵ ثانیه طول می‌کشد
        </p>
      </div>

      <style>{`
        @keyframes pulse-ring {
          0% { transform: scale(0.9); opacity: 0.3; }
          50% { transform: scale(1.15); opacity: 0.15; }
          100% { transform: scale(0.9); opacity: 0.3; }
        }
        @keyframes bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .search-loader-dots {
          display: inline-flex;
          letter-spacing: 0;
          margin-inline-start: 2px;
        }
        .search-loader-dots span {
          animation: search-loader-dot 1.2s ease-in-out infinite;
          opacity: 0.25;
        }
        .search-loader-dots span:nth-child(2) {
          animation-delay: 0.18s;
        }
        .search-loader-dots span:nth-child(3) {
          animation-delay: 0.36s;
        }
        @keyframes search-loader-dot {
          0%, 80%, 100% { opacity: 0.25; }
          35% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function easeOutCubic(value: number) {
  return 1 - Math.pow(1 - value, 3);
}
